/**
 * Auth slice — login state, session user, logout.
 *
 * Follows the admin-ui shape (`store/auth/authSlice.ts`: `loginUser` /
 * `logoutUser` thunks + `isAuthenticated` boolean), with two workspace
 * differences from the design doc:
 * - the session user comes from decoding the access-token claims at login time
 *   (design §7 — no `/auth/me` round-trip), and
 * - `bootstrapAuth` runs the proactive refresh on mount (design §13.1).
 *
 * `auth/sessionExpired` is dispatched as a bare action by the RTK Query reauth
 * base query (`store/apiSlice.ts`) — a plain type keeps that file free of an
 * import cycle back into this slice.
 */

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

import {
  ensureFreshSession,
  getUser,
  isAuthenticated,
  login,
  logout,
  takeSessionExpiredNotice,
} from '@/lib/auth';
import { apiSlice } from '@/store/apiSlice';
import type { RootState } from '@/store/store';
import type { LoginRequest, User } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Login-page error copy (design §7); `null` when there is nothing to show. */
  error: string | null;
  /** True after an involuntary logout (failed refresh) — drives the login notice. */
  sessionExpired: boolean;
}

function readInitialState(): AuthState {
  return {
    user: getUser(),
    isAuthenticated: isAuthenticated(),
    isLoading: false,
    error: null,
    // Consumed exactly once, here at store creation, so a hard redirect to the
    // login page still explains itself ("Your session expired…").
    sessionExpired: takeSessionExpiredNotice(),
  };
}

/** Design §7 copy: what went wrong *and* how to fix it. */
export function loginErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401) {
      return 'Incorrect email or password. Check your credentials and try again.';
    }
    if (!error.response || status === undefined || status >= 500) {
      return "Can't reach the server. Check your connection and try again.";
    }
  }
  return 'Sign-in failed. Please try again.';
}

export const loginUser = createAsyncThunk<User | null, LoginRequest, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      // `login()` stores the token pair and persists the decoded session user.
      await login(credentials);
      return getUser();
    } catch (error) {
      return rejectWithValue(loginErrorMessage(error));
    }
  },
);

export const logoutUser = createAsyncThunk<void>('auth/logout', async (_, { dispatch }) => {
  // Best-effort server-side revoke (`POST /auth/logout`); local state always clears.
  await logout();
  dispatch(apiSlice.util.resetApiState());
});

interface BootstrapResult {
  user: User | null;
  authenticated: boolean;
}

/**
 * Hydrates the slice from localStorage on first protected mount and refreshes
 * proactively when the access token is about to expire, so the first
 * authenticated request never rides a dead token.
 */
export const bootstrapAuth = createAsyncThunk<BootstrapResult>(
  'auth/bootstrap',
  async (_, { dispatch }) => {
    if (!isAuthenticated()) {
      return { user: null, authenticated: false };
    }

    const user = await ensureFreshSession();
    if (!user && !isAuthenticated()) {
      // Proactive refresh failed → the family is gone; show the expiry notice.
      dispatch(sessionExpired());
      return { user: null, authenticated: false };
    }

    return { user: user ?? getUser(), authenticated: isAuthenticated() };
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState: readInitialState(),
  reducers: {
    clearError(state) {
      state.error = null;
    },
    /** Called by the reauth base query after a failed/absent refresh. */
    sessionExpired(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.sessionExpired = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Sign-in failed. Please try again.';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(bootstrapAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = action.payload.authenticated;
      });
  },
});

export const { clearError, sessionExpired } = authSlice.actions;

export const selectAuth = (state: RootState) => state.auth;
export const selectCurrentUser = createSelector(selectAuth, (auth) => auth.user);
export const selectIsAuthenticated = createSelector(selectAuth, (auth) => auth.isAuthenticated);

export default authSlice.reducer;
